import api from 'services/api';

export interface AgentLogFile {
  file_name: string;
  size_bytes: number;
  modified_at: string | null;
}

export interface WorkerLogFilesResponse {
  files: AgentLogFile[];
  info?: string | null;
}

const parseDownloadFileName = (contentDisposition: string | undefined, fallbackName: string): string => {
  if (!contentDisposition) return fallbackName;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = contentDisposition.match(/filename=([^;\n]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim();
  }

  return fallbackName;
};

const parseBlobErrorMessage = async (error: any, fallbackMessage: string): Promise<string> => {
  const blobPayload = error?.response?.data;

  if (blobPayload instanceof Blob) {
    const text = await blobPayload.text();
    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || fallbackMessage;
    } catch {
      return text || fallbackMessage;
    }
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  return fallbackMessage;
};

export const fetchWorkerLogFiles = async (workerId: number | string): Promise<WorkerLogFilesResponse> => {
  const response = await api.get(`/workers/${workerId}/log_files`);
  const files = response?.data?.files;
  return {
    files: Array.isArray(files) ? files : [],
    info: typeof response?.data?.info === 'string' ? response.data.info : null,
  };
};

export const downloadWorkerLogFile = async (workerId: number | string, fileName: string): Promise<void> => {
  const response = await api.get(`/workers/${workerId}/log_files/${encodeURIComponent(fileName)}`, {
    responseType: 'blob',
  });

  const parsedFileName = parseDownloadFileName(response.headers?.['content-disposition'], fileName);

  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = parsedFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

export const getWorkerLogDownloadError = async (error: any): Promise<string> => {
  return parseBlobErrorMessage(error, 'Failed to download worker log file');
};
